import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const MACHINE_FCB = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/c6o7evxt_Untitled%20design.png";
const MACHINE_POSTMIX = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/bjofozej_ULTIMATE%20INFUSIONS-4.png";
const MACHINE_SLUSH = "https://customer-assets.emergentagent.com/job_17b76ed5-ea8c-4be2-90e7-e5b1550fa22e/artifacts/lyjj6knp_ULTIMATE%20INFUSIONS-5.png";

const machines = [
  { src: MACHINE_FCB, alt: "Ultimate Infusions FCB Machine", label: "Frozen Beverage" },
  { src: MACHINE_SLUSH, alt: "Ultimate Infusions Slush Machine", label: "Slush Beverage" },
  { src: MACHINE_POSTMIX, alt: "Ultimate Infusions Post Mix Machine", label: "Post Mix Beverage" },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });

  const orbY1 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const orbY2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % machines.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0B1D3A 0%, #1A365D 40%, #1E3A5F 70%, #0B1D3A 100%)",
      }}
    >
      {/* Subtle grid pattern - parallax */}
      <motion.div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "40px 40px",
          y: gridY,
        }}
      />

      {/* Glowing orbs - parallax drift */}
      <motion.div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-[#C8D616]/10 blur-[120px]" style={{ y: orbY1 }} />
      <motion.div className="absolute bottom-1/4 left-1/6 w-[300px] h-[300px] rounded-full bg-blue-500/10 blur-[100px]" style={{ y: orbY2 }} />

      <motion.div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-28 lg:pb-20 w-full" style={{ opacity: contentOpacity }}>
        <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <motion.div className="text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                <span className="w-2 h-2 rounded-full bg-[#C8D616] animate-pulse" />
                <span className="text-white/80 text-xs font-medium tracking-[0.12em]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  PIONEERING BEVERAGE TECHNOLOGY IN INDIA
                </span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-6"
            >
              Redefining India's{" "}
              <span className="text-[#C8D616]">Frozen & Carbonated</span>{" "}
              Beverage Experience
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
              className="text-base lg:text-lg text-slate-300 max-w-xl mb-8 leading-relaxed"
            >
              Next-generation frozen carbonated beverage systems backed by 40+ years of
              Australian expertise. High-margin, scalable solutions for retail, QSR,
              and hospitality.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45, ease: "easeOut" }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <Button
                onClick={() => scrollTo("#contact")}
                className="rounded-full bg-[#C8D616] text-[#0B1D3A] hover:bg-[#B5C314] px-8 py-6 text-base font-bold shadow-lg shadow-[#C8D616]/20"
                data-testid="hero-book-demo-btn"
              >
                Get In Touch
                <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button
                onClick={() => scrollTo("#products")}
                variant="outline"
                className="rounded-full border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base font-semibold"
                data-testid="hero-partner-btn"
              >
                Explore Products
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
              className="inline-flex bg-[#0a1628] rounded-xl border border-[#C8D616]/30 shadow-2xl"
            >
              <div className="px-5 sm:px-6 py-4 text-center border-r border-white/10" data-testid="hero-stat-years">
                <div className="text-xl sm:text-2xl lg:text-3xl font-black text-[#C8D616]">40+</div>
                <div className="text-[10px] sm:text-xs text-white/80 font-medium mt-1 uppercase tracking-wider">Years</div>
              </div>
              <div className="px-5 sm:px-6 py-4 text-center border-r border-white/10" data-testid="hero-stat-flavours">
                <div className="text-xl sm:text-2xl lg:text-3xl font-black text-[#C8D616]">20+</div>
                <div className="text-[10px] sm:text-xs text-white/80 font-medium mt-1 uppercase tracking-wider">Flavours</div>
              </div>
              <div className="px-5 sm:px-6 py-4 text-center" data-testid="hero-stat-network">
                <div className="text-xl sm:text-2xl lg:text-3xl font-black text-[#C8D616] whitespace-nowrap">Pan India</div>
                <div className="text-[10px] sm:text-xs text-white/80 font-medium mt-1 uppercase tracking-wider">Network</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right visual - Machine Carousel - centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: "easeOut" }}
            className="relative flex flex-col items-center"
          >
            <div className="relative w-full max-w-[380px] lg:max-w-[420px] aspect-[3/4] overflow-visible">
              <AnimatePresence mode="wait">
                <motion.img
                  key={`machine-${current}`}
                  src={machines[current].src}
                  alt={machines[current].alt}
                  className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl"
                  initial={{ opacity: 0, scale: 0.92, rotateY: 30 }}
                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                  exit={{ opacity: 0, scale: 0.92, rotateY: -30 }}
                  transition={{ duration: 0.7, ease: "easeInOut" }}
                  data-testid="hero-machine-img"
                />
              </AnimatePresence>
              {/* Glow behind machine */}
              <div className="absolute inset-0 bg-[#C8D616]/5 rounded-full blur-3xl scale-110 -z-10" />
            </div>
            {/* Label + dots - centered below machine */}
            <div className="mt-4 flex flex-col items-center gap-3">
              <div className="bg-slate-900/70 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                <motion.p
                  key={`label-${current}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-white font-medium uppercase tracking-widest"
                >
                  {machines[current].label}
                </motion.p>
              </div>
              <div className="flex gap-2 bg-slate-900/60 px-3 py-2 rounded-full">
                {machines.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      i === current ? "bg-[#C8D616] w-7" : "bg-white/50 hover:bg-white/70"
                    }`}
                    aria-label={`Show machine ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
}
